import type { ReactNode } from "react";

import type { Chip } from "../ride-journey.ts";
import { retryLabel, type ViewState } from "../view-state.ts";
import { Hero } from "./Hero.tsx";
import { PhoneShell, type ActionProps } from "./PhoneShell.tsx";

/**
 * The states the design does not draw, in the vocabulary the design does.
 *
 * `10 Rider App.dc.html` draws eleven screens and not one of them is a loading, forbidden, conflict
 * or offline state. The rules say to build every designed state and to add nothing that is not in
 * the design, and these four are neither designed nor optional: a phone on a Kenyan mobile network
 * reaches all of them, and a rider who has just pressed `Confirm ride` reaches two of them more
 * often than anybody would like.
 *
 * So each is rendered as an ordinary screen of this application — the same hero wash, the same
 * sheet, the same chip, the same 52dp action — rather than in a look nobody drew. A rider sees the
 * furniture they already know whatever has happened, and the words are flagged as this build's so
 * the design team can replace them without hunting.
 *
 * Three choices in here are worth stating, because each could have gone the convenient way:
 *
 *   - **Loading is not a spinner.** The design has no spinner anywhere, and the one screen that is
 *     about waiting — `4 Confirming` — is a dashed note rather than an animation. So loading is the
 *     design's `unknown` chip and a sentence.
 *   - **Forbidden names the scope.** A rider told `customer-app:read` can quote it to whoever
 *     administers their account; one told "access denied" opens a ticket somebody else has to
 *     translate.
 *   - **Conflict is its own state and not an error.** The one way a rider reaches a 409 here is
 *     pressing `Confirm ride` twice with terms that changed between the presses, which Document 06
 *     says is refused rather than answered from the first request. "That didn't work" would be a lie:
 *     something worked, once, and the rider needs to be told which.
 */

export interface StateScreenProps {
  /** The screen family whose stylesheet and hero height this borrows. */
  readonly screen: string;
  /** Omit to draw the explicit chip, title and sub below instead of a `ViewState`. */
  readonly state?: ViewState;
  readonly onRetry?: () => void;
  /** What the screen was trying to do, said in the sub-line of the loading state. */
  readonly purpose?: string;
  readonly chip?: Chip;
  readonly title?: string;
  readonly sub?: string;
  readonly flag?: string;
}

export function StateScreen(props: StateScreenProps): ReactNode {
  const drawn =
    props.state === undefined ? undefined : describeViewState(props.state, props.purpose ?? "");
  const retry = props.state === undefined ? undefined : retryLabel(props.state);

  const actions: ActionProps[] =
    retry === undefined || props.onRetry === undefined
      ? []
      : [{ label: retry, glyph: "refresh", primary: true, onPress: props.onRetry }];

  return (
    <PhoneShell
      screen={`${props.screen} screen--state`}
      hero={<Hero kind="none" />}
      chip={props.chip ?? drawn?.chip ?? { class: "unknown", label: "unknown" }}
      title={props.title ?? drawn?.title ?? ""}
      sub={props.sub ?? drawn?.sub ?? ""}
      actions={actions}
      flag={props.flag ?? "wording not in the design"}
    />
  );
}

export interface Drawn {
  readonly chip: Chip;
  readonly title: string;
  readonly sub: string;
}

/**
 * One state, as a screen.
 *
 * Total over `ViewState` rather than defaulted, so a state added to that union fails the build here
 * instead of rendering as a blank sheet.
 */
export function describeViewState(state: ViewState, purpose: string): Drawn {
  switch (state.kind) {
    case "loading":
      return {
        chip: { class: "idle", label: "checking" },
        title: "One moment",
        sub: purpose,
      };

    case "ready":
      return {
        chip: { class: "done", label: "ready" },
        title: "Ready",
        sub: purpose,
      };

    case "offline":
      return {
        chip: { class: "unknown", label: "no signal" },
        title: "We can't reach CityCab",
        sub: "Nothing has been lost. This will work again when your signal comes back.",
      };

    case "forbidden":
      return {
        chip: { class: "closed", label: "not permitted" },
        title: "Your sign-in doesn't allow this",
        sub:
          state.missingScopes.length > 0
            ? `It needs ${state.missingScopes.join(" and ")}. Ask whoever administers your account for that.`
            : "Ask whoever administers your account for access to this.",
      };

    case "error":
      return state.conflict
        ? {
            /*
             * The designed 409, which `CLAUDE.md` section 1 names among the states that must be
             * built. On this application it has one source: an `Idempotency-Key` reused with terms
             * that changed. Document 06 refuses that rather than answering from the first request,
             * and the two outcomes a rider could be in are "you have a ride" and "you have a
             * different ride" — so the screen says to look rather than offering a retry.
             */
            chip: { class: "caution", label: "conflict" },
            title: "That didn't go through twice",
            sub: `Something about this booking changed between your two taps, so it was refused rather than booked again. Check your rides before trying once more. (${state.code})`,
          }
        : {
            chip: { class: "failed", label: "failed" },
            title: "That didn't work",
            sub: `${state.message} (${state.code})`,
          };
  }
}
